import React, { useState, useEffect, useCallback, useRef } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";

import BackgroundShapes from "../components/ui-new/BackgroundShapes";
import Header from "../components/ui-new/Header";
import ChatHistory from "../components/ui-new/ChatHistory";
import ProductDetail from "../components/ProductDetails";
import useIsMobile from "../hooks/useIsMobile";
import useResultsHistory from "../hooks/useResultsHistory";
import { useModalContext } from "../components/contexts/ModalContext";
import { useUserSessions } from "../api/useUserSessions";
import { useAnonymousSession } from "../api/useAnonymousSession";

// Feature flag - must match ResultsPage
const USE_PROGRESSIVE_STREAMING = true;

const ChatLayout = ({ selectedProduct, setSelectedProduct }) => {
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isProductDetailOpen, setIsProductDetailOpenOpen] = useState(false);

  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { setIsModalOpen } = useModalContext();
  const messageRef = useRef(``);
  const scrollContainerRef = useRef(null);

  //chat
  const [message, setMessage] = useState(``);
  const [messages, setMessages] = useState([]);

  const [inferredPreferences, setInferredPreferences] = useState(null);

  const { getLast } = useResultsHistory(sessionId);

  const { isSignedIn, createUserSession, postUserSessionChat } =
    useUserSessions();
  const {
    hasAnonymousSession,
    createAnonymousSession,
    isAnonymousSession,
    postAnonymousChat,
  } = useAnonymousSession();

  const shouldSend = useRef(false);

  const updateMessage = (message) => {
    setMessage(message.target ? message.target.value : message);
  };

  // reset messages when sessionId changes
  useEffect(() => {
    if (messageRef.current) {
      setMessages([{ role: "user", content: messageRef.current }]);
      messageRef.current = ``;
    }
    if (!sessionId) {
      setMessages([]);
      setInferredPreferences(null);
    }
  }, [sessionId]);

  const sendMessage = useCallback(
    async (event) => {
      if (event && typeof event.preventDefault === "function") {
        event.preventDefault();
      }

      const messageToSend = message.trim();
      messageRef.current = messageToSend;
      const updatedMessages = [
        ...messages,
        { role: "user", content: messageToSend },
      ];
      setMessages(updatedMessages);
      setMessage(``);

      if (!sessionId) {
        const newSession =
          !isSignedIn && !hasAnonymousSession()
            ? await createAnonymousSession().catch(() => {
                setIsModalOpen(true);
              })
            : await createUserSession();

        console.log({ newSession });
        if (newSession) {
          navigate(`/chats/${newSession.sessionId}`);
          const isAnonymous = await isAnonymousSession(newSession.sessionId);
          const lastResults = getLast();

          const streamingOptions = USE_PROGRESSIVE_STREAMING ? { useStreaming: true } : {};
          const data = isAnonymous
            ? await postAnonymousChat(
                newSession.sessionId,
                messageToSend,
                lastResults,
                streamingOptions
              ).catch(() => {
                setIsModalOpen(true);
              })
            : await postUserSessionChat(
                newSession.sessionId,
                messageToSend,
                lastResults,
                streamingOptions
              );

          setMessages(data?.messages ?? []);
          setMessage(``);
        }
      } else {
        const isAnonymous = await isAnonymousSession(sessionId);

        const lastResults = getLast();
        const streamingOptions = USE_PROGRESSIVE_STREAMING ? { useStreaming: true } : {};
        const data = isAnonymous
          ? await postAnonymousChat(
              sessionId,
              messageToSend,
              lastResults,
              streamingOptions
            ).catch(() => {
              setIsModalOpen(true);
            })
          : await postUserSessionChat(sessionId, messageToSend, lastResults, streamingOptions);

        setMessages(data?.messages ?? []);
        setMessage(``);
      }
    },
    [
      createAnonymousSession,
      createUserSession,
      hasAnonymousSession,
      isAnonymousSession,
      message,
      messages,
      navigate,
      postAnonymousChat,
      postUserSessionChat,
      sessionId,
      getLast,
      setIsModalOpen,
      isSignedIn,
    ]
  );

  const applyMacro = (macro) => {
    updateMessage(macro);
    shouldSend.current = true;
  };

  useEffect(() => {
    if (shouldSend.current) {
      shouldSend.current = false;
      sendMessage();
    }
  }, [sendMessage]);

  useEffect(() => {
    if (selectedProduct) {
      setIsProductDetailOpenOpen(true);
      setIsHistoryOpen(false);
    }
  }, [selectedProduct]);

  const isMobile = useIsMobile();

  //scroll to bottom of chat when new message is sent
  useEffect(() => {
    if (scrollContainerRef.current) {
      // Scroll to the bottom of the scrollContainer
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [messages, isMobile]);

  return (
    <div className="flex min-h-[100dvh] bg-penta-grey-light">
      {/* Pentatonic: solid background, no gradient overlays */}
      <div className="fixed inset-0 -z-10">
        <BackgroundShapes />
      </div>

      {/* Header */}
      <Header
        onPanelClick={() => {
          setIsHistoryOpen(!isHistoryOpen);
          setIsProductDetailOpenOpen(false);
          setTimeout(() => {
            setSelectedProduct(null);
          }, 200);
        }}
        isHistoryOpen={isHistoryOpen}
        className="absolute top-0 left-0 w-full z-50"
      />

      <AnimatePresence>
        {isHistoryOpen && (
          <>
            {/* Mobile overlay - only visible on small screens */}
            {isMobile && (
              <motion.div
                initial={{ opacity: 0, backdropFilter: "blur(0px)" }}
                animate={{ opacity: 1, backdropFilter: "blur(20px)" }}
                exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
                transition={{ duration: 0.3 }}
                className="fixed inset-0 z-40 md:hidden"
                onClick={() => setIsHistoryOpen(false)}
              />
            )}

            {/* Sidebar */}
            <motion.aside
              initial={isMobile ? { opacity: 0 } : { width: 0 }}
              animate={isMobile ? { opacity: 1 } : { width: "400px" }}
              exit={isMobile ? { opacity: 0 } : { width: 0 }}
              transition={{ duration: 0.3 }}
              className={`${
                isMobile
                  ? "fixed w-full bg-white/20"
                  : "relative w-[550px] bg-white/80"
              } h-[100vh] z-50 overflow-x-hidden`}
            >
              <ChatHistory
                setIsHistoryOpen={setIsHistoryOpen}
                currentSessionId={sessionId}
              />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content Area - Add relative positioning and z-index */}
      <main className="flex-1 relative z-20">
        <Outlet
          context={{
            messages,
            message,
            updateMessage,
            sendMessage,
            applyMacro,
            setMessages,
            sessionId,
            scrollContainerRef,
            inferredPreferences,
            setInferredPreferences,
          }}
        />
      </main>

      <AnimatePresence>
        {isProductDetailOpen && selectedProduct && (
          <>
            {/* Mobile overlay - only visible on small screens */}
            {isMobile && (
              <motion.div
                initial={{ opacity: 0, backdropFilter: "blur(0px)" }}
                animate={{ opacity: 1, backdropFilter: "blur(20px)" }}
                exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
                transition={{ duration: 0.3 }}
                className="fixed inset-0 z-40 md:hidden"
                onClick={() => setIsProductDetailOpenOpen(false)}
              />
            )}

            {/* Sidebar */}
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.3 }}
              className={`${
                isMobile
                  ? "fixed w-full bg-white h-[100vh]"
                  : "relative min-w-[400px] max-w-[400px] bg-white h-[100vh]"
              } z-50 overflow-x-scroll`}
            >
              {selectedProduct && (
                <ProductDetail
                  product={selectedProduct}
                  setSelectedProduct={setSelectedProduct}
                />
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ChatLayout;
